import { createAuthClient } from '../utils/apiClient';
import { getMyInvitations } from './invitationService';

/**
 * Get comprehensive dashboard statistics AND invitation stats in a single optimized call
 * This reduces API calls from 11+ down to 4 by sharing invitation data between functions
 * @returns {Promise} Promise with both dashboard stats and invitation stats
 */
export const getAllDashboardData = async () => {
  try {
    // Create authenticated API client
    const client = createAuthClient();

    // Make all API calls in parallel to reduce latency
    const [
      statsResponse,
      pendingReceivedResponse,
      sentResponse,
      acceptedResponse,
      declinedResponse,
      completedResponse
    ] = await Promise.all([
      client.get('/users/stats'),
      getMyInvitations({ type: 'received', status: 'pending' }),
      getMyInvitations({ type: 'sent' }),
      getMyInvitations({ status: 'accepted' }),
      getMyInvitations({ status: 'declined' }),
      getMyInvitations({ status: 'completed' })
    ]);

    if (!statsResponse.data.success) {
      throw new Error(statsResponse.data.error?.message || 'Failed to fetch statistics');
    }

    // Extract invitation data
    const pendingReceivedInvitations = pendingReceivedResponse.success
      ? pendingReceivedResponse.data.invitations
      : [];
    const sentInvitations = sentResponse.success
      ? sentResponse.data.invitations
      : [];
    const acceptedInvitations = acceptedResponse.success
      ? acceptedResponse.data.invitations
      : [];
    const declinedInvitations = declinedResponse.success
      ? declinedResponse.data.invitations
      : [];
    const completedInvitations = completedResponse.success
      ? completedResponse.data.invitations
      : [];

    // Calculate counts
    const pendingRequests = pendingReceivedInvitations.length;
    const totalMatches = acceptedInvitations.length;
    const totalSentInvitations = sentInvitations.length;
    const completedMeetings = completedInvitations.length;

    // Calculate total hours from completed sessions
    // Each session is 30 minutes (0.5 hours)
    const totalHours = completedInvitations.reduce((total, inv) => {
      const duration = inv.sessionDetails?.duration || 30; // Default 30 minutes
      return total + (duration / 60); // Convert minutes to hours
    }, 0);

    // Filter pending sent from sent invitations
    const pendingSentInvitations = sentInvitations.filter(inv => inv.status === 'pending');

    // Combine dashboard stats
    const dashboardStats = {
      ...statsResponse.data.data,
      pendingRequests,
      totalMatches,
      totalSentInvitations,
      completedMeetings,
      totalHours: Math.round(totalHours * 10) / 10 // Round to 1 decimal place
    };

    // Combine invitation stats
    const invitationStats = {
      pendingReceived: pendingRequests,
      pendingSent: pendingSentInvitations.length,
      accepted: totalMatches,
      declined: declinedInvitations.length,
      total: pendingRequests + pendingSentInvitations.length + totalMatches + declinedInvitations.length
    };

    return {
      success: true,
      dashboardStats,
      invitationStats,
      // Return the actual invitation arrays for use by Dashboard component
      invitations: {
        pendingReceived: pendingReceivedInvitations,
        sent: sentInvitations,
        accepted: acceptedInvitations,
        declined: declinedInvitations,
        completed: completedInvitations
      }
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);

    return {
      success: false,
      error: {
        message: error.message || 'Failed to fetch dashboard data'
      },
      dashboardStats: {
        pendingRequests: 0,
        totalMatches: 0,
        completedMeetings: 0,
        profileViews: 0,
        totalSessions: 0,
        totalHours: 0
      },
      invitationStats: {
        pendingReceived: 0,
        pendingSent: 0,
        accepted: 0,
        declined: 0,
        total: 0
      },
      invitations: {
        pendingReceived: [],
        sent: [],
        accepted: [],
        declined: [],
        completed: []
      }
    };
  }
};

/**
 * Get comprehensive dashboard statistics
 * Combines data from multiple endpoints for the dashboard
 * @deprecated Use getAllDashboardData() instead to reduce API calls
 * @returns {Promise} Promise with the dashboard stats
 */
export const getDashboardStats = async () => {
  const result = await getAllDashboardData();
  return {
    success: result.success,
    data: result.dashboardStats,
    error: result.error
  };
};

/**
 * Get summary of invitation statistics
 * @deprecated Use getAllDashboardData() instead to reduce API calls
 * @returns {Promise} Promise with invitation counts
 */
export const getInvitationStats = async () => {
  const result = await getAllDashboardData();
  return {
    success: result.success,
    data: result.invitationStats,
    error: result.error
  };
}; 